import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from '../../utils/toast';

const TaskTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    taskName: '',
    assignedTo: [],
    assignedGroups: [],
    deadlineDays: 7,
    status: 'pending',
    description: '',
    verificationTypes: []
  });

  useEffect(() => {
    fetchTemplates();
    fetchUsers();
    fetchGroups();
    fetchVerificationTypes();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/task-templates');
      setTemplates(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch task templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await api.get('/groups');
      setGroups(res.data?.data || res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchVerificationTypes = async () => {
    try {
      const res = await api.get('/admin/verification-types');
      setVerificationTypes(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setFormData({
      name: '',
      taskName: '',
      assignedTo: [],
      assignedGroups: [],
      deadlineDays: 7,
      status: 'pending',
      description: '',
      verificationTypes: []
    });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setFormData({
      name: t.name,
      taskName: t.taskName,
      assignedTo: (t.assignedTo || []).map(u => u._id),
      assignedGroups: (t.assignedGroups || []).map(g => g._id),
      deadlineDays: t.deadlineDays ?? 7,
      status: t.status || 'pending',
      description: t.description || '',
      verificationTypes: (t.verificationTypes || []).map(v => v.id?._id || v.id).filter(Boolean)
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...formData,
        verificationTypes: formData.verificationTypes
      };
      if (editing) {
        await api.put(`/admin/task-templates/${editing._id}`, payload);
        toast.success('Task template updated');
      } else {
        await api.post('/admin/task-templates', payload);
        toast.success('Task template created');
      }
      setShowModal(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save task template');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task template?')) return;
    try {
      await api.delete(`/admin/task-templates/${id}`);
      toast.success('Task template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.includes(userId)
        ? prev.assignedTo.filter(id => id !== userId)
        : [...prev.assignedTo, userId]
    }));
  };

  const toggleGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      assignedGroups: prev.assignedGroups.includes(groupId)
        ? prev.assignedGroups.filter(id => id !== groupId)
        : [...prev.assignedGroups, groupId]
    }));
  };

  const toggleVerificationType = (vtId) => {
    setFormData(prev => ({
      ...prev,
      verificationTypes: prev.verificationTypes.includes(vtId)
        ? prev.verificationTypes.filter(id => id !== vtId)
        : [...prev.verificationTypes, vtId]
    }));
  };

  const formatAssignees = (t) => {
    const u = (t.assignedTo || []).map(x => x.firstName || x).join(', ');
    const g = (t.assignedGroups || []).map(x => x.name || x).join(', ');
    return [u, g].filter(Boolean).join('; ') || '—';
  };

  return (
    <div className="px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Task Templates</h2>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Add Task Template
        </button>
      </div>

      {loading && templates.length === 0 ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="overflow-x-auto bg-white shadow rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name (Prebuilt)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assign to</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline (days)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((t) => (
                <tr key={t._id}>
                  <td className="px-4 py-3 text-sm text-gray-900">{t.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{t.taskName}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{formatAssignees(t)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{t.deadlineDays}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{t.status}</td>
                  <td className="px-4 py-3 text-right text-sm">
                    <button type="button" onClick={() => openEdit(t)} className="text-indigo-600 hover:text-indigo-900 mr-3">Edit</button>
                    <button type="button" onClick={() => handleDelete(t._id)} className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {templates.length === 0 && (
            <p className="text-center text-gray-500 py-8">No task templates yet. Add one to use when creating tasks.</p>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSubmit} className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{editing ? 'Edit Task Template' : 'Add Task Template'}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name of prebuilt task</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Task name</label>
                    <input
                      type="text"
                      value={formData.taskName}
                      onChange={e => setFormData(prev => ({ ...prev, taskName: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Assign to user(s)</label>
                    <div className="mt-1 border border-gray-300 rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                      {users.map(u => (
                        <label key={u._id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.assignedTo.includes(u._id)}
                            onChange={() => toggleUser(u._id)}
                            className="rounded border-gray-300"
                          />
                          <span className="ml-2 text-sm">{u.firstName} {u.lastName}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Assign to group(s)</label>
                    <div className="mt-1 border border-gray-300 rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                      {groups.map(g => (
                        <label key={g._id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.assignedGroups.includes(g._id)}
                            onChange={() => toggleGroup(g._id)}
                            className="rounded border-gray-300"
                          />
                          <span className="ml-2 text-sm">{g.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Deadline (days from today)</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.deadlineDays}
                      onChange={e => setFormData(prev => ({ ...prev, deadlineDays: Number(e.target.value) || 0 }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={formData.status}
                      onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Verification type(s)</label>
                    <div className="mt-1 border border-gray-300 rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                      {verificationTypes.map(vt => (
                        <label key={vt._id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.verificationTypes.includes(vt._id)}
                            onChange={() => toggleVerificationType(vt._id)}
                            className="rounded border-gray-300"
                          />
                          <span className="ml-2 text-sm">{vt.label || vt.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskTemplates;
